import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import axiosInstance from "../../utils/networkWrapper";

export type EmployeeChangeLogField =
	| "first_name"
	| "last_name"
	| "role"
	| "job_ids"
	| "employment_type"
	| "email"
	| "phone_number"
	| "active"
	| "status_decision";

export interface EmployeeChangelogEntry {
	id: string;
	nurseId: number;
	nurseName: string;
	eventType: "Insert" | "Update" | "Delete";
	changedFields: Record<EmployeeChangeLogField, unknown> | null;
	changedBy: string | undefined;
	source: string | undefined;
	changedAt: string;
	isImportant: boolean;
	departmentId: string | undefined;
}

const EMPLOYEE_CHANGELOG_QUERY_KEY = "employee-changelog";

export const useEmployeeChangelogQuery = ({
	start,
	end,
	nurseId,
	changedBy,
}: {
	start: DateTime;
	end: DateTime;
	nurseId?: number;
	changedBy?: string;
}): UseQueryResult<EmployeeChangelogEntry[], Error> => {
	const { chosenDepartment, showErrorSnackbar } = useHospitalData();

	return useQuery({
		queryKey: [EMPLOYEE_CHANGELOG_QUERY_KEY, chosenDepartment?.department_id, start.toString(), end.toString(), nurseId, changedBy],
		queryFn: async () => {
			trackEvent("employee_changelog_fetch_start", { department_id: chosenDepartment?.department_id });

			const { data } = await axiosInstance.get<EmployeeChangelogEntry[]>(`/employee-changelog/departments/${chosenDepartment.department_id}`, {
				params: {
					start: start.toFormat("yyyy-MM-dd"),
					end: end.toFormat("yyyy-MM-dd"),
					nurse_id: nurseId,
					changed_by: changedBy,
				},
			});

			trackEvent("employee_changelog_fetch_success", { department_id: chosenDepartment?.department_id, entry_count: data.length });
			return data;
		},
		staleTime: 60 * 1000,
		throwOnError: false,
		enabled: !!chosenDepartment?.department_id,
		meta: {
			onError: (error: unknown) => {
				showErrorSnackbar("Failed to load employee changelog", { action: "failed to fetch employee changelog" }, error);
				trackErrorEvent("employee_changelog_fetch_error", error, {
					department_id: chosenDepartment?.department_id,
					error_source: "API",
				});
			},
		},
	});
};
